"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RetailLocation = void 0;
const class_validator_1 = require("class-validator");
const moment = require("moment-timezone");
const _ = require("lodash");
const Location_1 = require("../../../../apis/spg/models/Location");
const currentUnixTimestamp_1 = require("../../../../apis/spg/util/currentUnixTimestamp");
const distanceBetweenTwoLocations_1 = require("../../../../helpers/distanceBetweenTwoLocations");
const time_1 = require("../../../../helpers/time");
const TSalesOrderType_1 = require("../types/TSalesOrderType");
const delivery_1 = require("../types/delivery");
/**
 * A location that provides goods for sale.
 *  - Has defined business hours where orders can be placed.
 *  TODO: add a flag to allow orders to be placed at any time for example a
 *  TODO: for a computer store
 *  - Can define what ordering service they provide ("pick up" | "delivery")
 *  - They also manage inventory of the products they sell and the components
 *  that they are made up of
 */
class RetailLocation extends Location_1.Location {
    get isOpen() {
        const now = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        const { openingTime, closingTime } = this.getOperatingDetailsForTime(now);
        return now >= openingTime && now <= closingTime;
    }
    get currentTime() {
        return moment.tz(this.timezone.timeZoneId).unix();
    }
    get canDeliver() {
        return (this.address.lat &&
            this.address.lng &&
            this.orderingStatus.config.availableForDelivery &&
            this.orderingStatus.location.availableForDelivery);
    }
    get canPickup() {
        return (this.orderingStatus.config.availableForPickup &&
            this.orderingStatus.location.availableForPickup);
    }
    get isOnline() {
        return (this.canDeliver || this.canPickup) && this.isOpen;
    }
    get isOperating() {
        return this.canDeliver || this.canPickup;
    }
    get commsData() {
        return Object.assign({}, this.serialize());
    }
    get locationImageUrl() {
        return this.imageUrl || env.baseLocationImageUrl;
    }
    getDirectionsUrl(lat, lng) {
        const { address: { lat: restaurantLat, lng: restaurantLng } } = this;
        return `https://www.google.com.au/maps/dir/${lat},${lng}/${restaurantLat},${restaurantLng}/`;
    }
    isWithinOpeningHours(timestamp) {
        if (timestamp === null) {
            return false;
        }
        const { openingTime, closingTime } = this.getOperatingDetailsForTime(timestamp);
        return timestamp >= openingTime && timestamp <= closingTime;
    }
    canFulfilDuringOpeningHours(timestamp) {
        const tenMinutesInSeconds = 600;
        if (timestamp === null) {
            return false;
        }
        const { openingTime, closingTime } = this.getOperatingDetailsForTime(timestamp);
        // estimated pickup time for delivery orders <= closingTime - 10 minutes margin before closing time
        return (timestamp >= openingTime && timestamp <= closingTime - tenMinutesInSeconds);
    }
    getOperatingDetailsForTime(timestamp) {
        if (!this.openingHours) {
            console.warn(`${this.title} has no opening hours set`);
            return {
                openingTime: null,
                closingTime: null,
                isOpenToday: false
            };
        }
        const { timezone: { timeZoneId } } = this;
        const operationalDay = this.getOperationalDay(timestamp);
        const momentDate = moment(operationalDay);
        const dayOfWeek = momentDate.day();
        // @ts-ignore
        const openingHourForDayOfWeek = this.openingHours.find((openingHour) => openingHour.open.day === dayOfWeek);
        if (openingHourForDayOfWeek === undefined) {
            return {
                openingTime: null,
                closingTime: null,
                isOpenToday: false
            };
        }
        const { open: { time: openTime }, close: { time: closingTime } } = openingHourForDayOfWeek;
        const momentOpening = (0, time_1.convertIsoDateAndHoursToLocalMomentDate)(operationalDay, openTime, timeZoneId);
        const momentClosing = (0, time_1.convertIsoDateAndHoursToLocalMomentDate)(operationalDay, closingTime, timeZoneId);
        // If closing is before opening, closing must be past midnight, so we add an extra day
        if (momentClosing.isBefore(momentOpening)) {
            momentClosing.add(1, "d");
        }
        return {
            openingTime: momentOpening.unix(),
            closingTime: momentClosing.unix(),
            isOpenToday: true
        };
    }
    getLocationsDistance(latLng) {
        if (!this.address.lat || !this.address.lng)
            return null;
        return (0, distanceBetweenTwoLocations_1.distanceBetweenTwoLocations)(latLng, {
            lat: this.address.lat,
            lng: this.address.lng
        });
    }
    isWithinDeliveryDistance(latLng) {
        const distanceToLocation = this.getLocationsDistance(latLng);
        return (!_.isNaN(distanceToLocation) &&
            distanceToLocation <= this.maxDeliveryDistance);
    }
    canOrderAtRetailLocation(orderType) {
        const { canPickup, canDeliver } = this;
        return ((orderType === TSalesOrderType_1.TSalesOrderType.Pickup && canPickup) ||
            (orderType === TSalesOrderType_1.TSalesOrderType.Delivery && canDeliver));
    }
    filterSensitiveData() {
        const clone = super.filterSensitiveData();
        delete clone.pricingCodes;
        return clone;
    }
    serialize() {
        return Object.assign(Object.assign({}, super.serialize()), { retailLocationId: this.retailLocationId, phoneNumber: this.phoneNumber, email: this.email, orderingStatus: this.orderingStatus, registrationNumber: this.registrationNumber, paymentGatewayId: this.paymentGatewayId, stripePaymentGatewayId: this.stripePaymentGatewayId, deliveryType: this.deliveryType, maxDeliveryDistance: this.maxDeliveryDistance, deliveryCost: this.deliveryCost, serviceFee: this.serviceFee, minimumDeliveryAmount: this.minimumDeliveryAmount, maximumOrderValue: this.maximumOrderValue, imageUrl: this.imageUrl, pricingCodes: this.pricingCodes, orderingTimes: this.orderingTimes });
    }
    static unserialize(data) {
        const newRetailLocation = super.unserialize(data);
        newRetailLocation.retailLocationId = data.retailLocationId;
        newRetailLocation.phoneNumber = data.phoneNumber;
        newRetailLocation.email = data.email;
        newRetailLocation.orderingStatus = data.orderingStatus;
        newRetailLocation.registrationNumber = data.registrationNumber;
        newRetailLocation.paymentGatewayId = data.paymentGatewayId;
        newRetailLocation.stripePaymentGatewayId = data.stripePaymentGatewayId;
        newRetailLocation.deliveryType = data.deliveryType;
        newRetailLocation.maxDeliveryDistance = data.maxDeliveryDistance;
        newRetailLocation.deliveryCost = data.deliveryCost;
        newRetailLocation.serviceFee = data.serviceFee;
        newRetailLocation.minimumDeliveryAmount = data.minimumDeliveryAmount;
        newRetailLocation.maximumOrderValue = data.maximumOrderValue;
        newRetailLocation.orderingTimes = data.orderingTimes;
        newRetailLocation.imageUrl = data.imageUrl;
        newRetailLocation.pricingCodes = data.pricingCodes;
        return newRetailLocation;
    }
}
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "retailLocationId", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "phoneNumber", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsEmail)(),
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "email", void 0);
__decorate([
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "orderingStatus", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "registrationNumber", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "stripePaymentGatewayId", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "paymentGatewayId", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "maxDeliveryDistance", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)(),
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.IsIn)(Object.values(delivery_1.TDeliveryOptions))
], RetailLocation.prototype, "deliveryType", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "deliveryCost", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], RetailLocation.prototype, "serviceFee", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], RetailLocation.prototype, "minimumDeliveryAmount", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], RetailLocation.prototype, "maximumOrderValue", void 0);
__decorate([
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "orderingTimes", void 0);
__decorate([
    (0, class_validator_1.IsNotEmpty)()
], RetailLocation.prototype, "pricingCodes", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], RetailLocation.prototype, "imageUrl", void 0);
exports.RetailLocation = RetailLocation;
//# sourceMappingURL=RetailLocation.js.map